import * as React from "react";
import { cn } from "@/lib/utils";

// New tone values (spec) + backward-compat aliases from old API
type Tone = "neutral" | "teal" | "coral" | "amber" | "success" | "brand" | "warning" | "danger";

const toneMap: Record<Tone, { iconBg: string; iconFg: string }> = {
  neutral: { iconBg: "bg-muted",           iconFg: "text-muted-foreground" },
  teal:    { iconBg: "bg-primary/10",      iconFg: "text-primary"          },
  coral:   { iconBg: "bg-secondary/10",    iconFg: "text-secondary"        },
  amber:   { iconBg: "bg-brand-amber/15",  iconFg: "text-brand-amber"      },
  success: { iconBg: "bg-success/10",      iconFg: "text-success"          },
  // backward-compat aliases
  brand:   { iconBg: "bg-primary/10",      iconFg: "text-primary"          },
  warning: { iconBg: "bg-brand-amber/15",  iconFg: "text-brand-amber"      },
  danger:  { iconBg: "bg-destructive/10",  iconFg: "text-destructive"      },
};

export interface StatCardProps {
  icon?: React.ElementType;
  label: string;
  value: string | number | React.ReactNode;
  tone?: Tone;
  trend?: string;
  className?: string;
}

export function StatCard({
  icon: Icon,
  label,
  value,
  tone = "neutral",
  trend,
  className,
}: StatCardProps) {
  const t = toneMap[tone] ?? toneMap.neutral;

  return (
    <div
      className={cn(
        "bg-card rounded-2xl border border-border shadow-sm p-4 sm:p-6 hover:shadow-md transition-shadow",
        className
      )}
    >
      <div className="flex items-start">
        {Icon && (
          <span
            className={cn(
              "h-10 w-10 rounded-xl flex items-center justify-center shrink-0",
              t.iconBg
            )}
          >
            <Icon className={cn("h-5 w-5", t.iconFg)} />
          </span>
        )}
        {trend && (
          // green-700 (~4.9:1 on white) passes AA; text-success (#10B981) was
          // ~2.8:1 as small trend text.
          <span className="ml-auto text-xs font-medium text-green-700">
            {trend}
          </span>
        )}
      </div>
      <p className="mt-4 text-3xl font-bold text-foreground tracking-tight">
        {value}
      </p>
      <p className="mt-1 text-sm text-muted-foreground">{label}</p>
    </div>
  );
}
